<template>
  <VirtualListGrid
    :items="items"
    :itemSize="itemSize"
    :scrollOffsetX="640"
    ref="list"
    :key="listKey"
  >
    <template slot="headerSticky">
      <div class="c-virtual-list__head keyword">
        <div class="c-virtual-list__col check"></div>
        <ThSort
          v-model="temp.sort"
          name="keyword"
          orderby="asc"
          tag="div"
          type="string"
          class="c-virtual-list__col keyword c-text--center"
          @change="change"
        >
          キーワード
        </ThSort>
        <ThSort
          v-model="temp.sort"
          name="url"
          orderby="asc"
          tag="div"
          type="string"
          class="c-virtual-list__col url c-text--center"
          @change="change"
        >
          URL
        </ThSort>
        <ThSort
          v-model="temp.sort"
          name="taskDoneDate"
          orderby="desc"
          tag="div"
          type="date"
          class="c-virtual-list__col task c-text--center"
          @change="change"
        >
          直近のタスク
        </ThSort>
      </div>
    </template>
    <template slot="header">
      <div>
        <div class="c-virtual-list__head months">
          <div
            v-for="(m, index) in dates.months"
            :key="index"
            class="c-virtual-list__col month"
            :style="{ width: m.width }"
          >
            <EllipsisPopover placement="top" size="sm">
              {{ m.label }}
            </EllipsisPopover>
          </div>
        </div>
        <div class="c-virtual-list__head days">
          <div
            v-for="(d, index) in dates.days"
            :key="index"
            class="c-virtual-list__col day"
          >
            {{ d.label }}
          </div>
        </div>
      </div>
    </template>
    <template slot="before">
      <div v-if="isLoading" class="c-virtual-list__guide">
        <div class="c-virtual-list__col">
          <Loading variant="inline" />
        </div>
      </div>
      <div v-else-if="!usage.current" class="p-results-check-guide">
        <p class="p-results-check-guide__lead">
          キーワードが登録されていません。まずはキーワードを登録しましょう。
        </p>
        <ButtonRectangle
          iconLeft="plus"
          priority="high"
          size="large"
          @click="openModalAddKeyword"
        >
          キーワード登録
        </ButtonRectangle>
      </div>
      <div v-else-if="!items.length" class="c-virtual-list__guide">
        <div class="c-virtual-list__col c-text--center">
          条件に合うデータがありません。
        </div>
      </div>
    </template>
    <template v-slot="{ item }">
      <ListItemKeywordV2
        :key="item.id"
        :checked="checked"
        :dates="dates"
        :dateItems="dateItems"
        :item="item"
        :rankItem="rankItems[item.id]"
        :project="project"
        :selectedId="selectedId"
        @check="updateChecked"
        @select="select"
        @displayItem="displayItem"
      />
    </template>
  </VirtualListGrid>
</template>

<script>
import ButtonRectangle from '@/components/ButtonRectangle';
import EllipsisPopover from '@/components/EllipsisPopover';
import ListItemKeywordV2 from '@/components/siteImportantKeywordGA4/ListItemKeywordV2';
import Loading from '@/components/Loading';
import ThSort from '@/components/ThSort';
import VirtualListGrid from '@/components/VirtualListGrid';
import utils from '@/utils';

export default {
  props: {
    checked: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    rankItems: {
      type: Object,
      required: true
    },
    period: {
      type: Array,
      required: true
    },
    project: {
      type: Object,
      required: true
    },
    selectedId: {
      type: String,
      required: true
    },
    sort: {
      type: Object,
      required: true
    },
    usage: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    listKey: {
      type: String,
      required: false,
      default: 'listKeyword'
    }
  },
  components: {
    ButtonRectangle,
    EllipsisPopover,
    ListItemKeywordV2,
    Loading,
    ThSort,
    VirtualListGrid
  },
  data() {
    return {
      itemSize: 43,
      itemWidth: 30,
      temp: {
        sort: Object.assign({}, this.sort)
      }
    };
  },
  computed: {
    dateItems() {
      const dateItems = this.dates.days.map(day => {
        const key = utils.formatDate(day.date, 'YYYY-MM-DD');
        return {
          id: key,
          date: day.date
        };
      });
      return dateItems;
    },
    dates() {
      const obj = {
        months: [],
        days: []
      };
      const startDate = new Date(this.period[0]);
      const endDate = new Date(this.period[1]);
      const total = utils.getDays(startDate, endDate);
      const d = new Date(startDate);

      for (let i = 0; i <= total; i++) {
        if (d.getDate() === 1 || i === 0) {
          let lastDate = utils.getEndDate(d, 'month');
          if (lastDate.getTime() > endDate.getTime()) {
            lastDate = new Date(endDate);
          }
          const days = utils.getDays(d, lastDate) + 1;
          const width = days * this.itemWidth + 'px';
          obj.months.push({
            days: days,
            label: utils.formatDate(d, 'YYYY.MM'),
            width: width
          });
        }
        obj.days.push({
          date: new Date(d),
          label: utils.formatDate(d, 'D')
        });
        d.setDate(d.getDate() + 1);
      }
      return obj;
    }
  },
  watch: {
    dates: {
      handler() {
        this.$nextTick(() => {
          this.$refs.list.resize();
          this.$refs.list.scrollToEnd();
        });
      },
      immediate: true
    },
    sort: {
      handler(value) {
        this.temp.sort = Object.assign({}, value);
      }
    }
  },
  methods: {
    change() {
      this.$emit('sort', this.temp.sort);
    },
    select(item) {
      this.$emit('select', item);
    },
    updateChecked(checked) {
      this.$emit('check', checked);
    },
    displayItem(id) {
      this.$emit('displayItem', id);
    },
    getRankItemPositions(keywordId, dateId) {
      const rankItem = this.rankItems[keywordId];
      if (rankItem && rankItem.positions && rankItem.positions[dateId]) {
        return rankItem.positions[dateId];
      }
      return undefined;
    },
    openModalAddKeyword() {
      this.$emit('openModalAddKeyword');
    }
  }
};
</script>
